import { Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { staffApi, errMsg, API } from "@/lib/api";
import { DEFAULT_SLIDE_IMAGES } from "@/lib/content";

export const Card = ({ title, hint, children }) => (
  <div className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-6">
    <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
    {hint && <p className="mt-1 text-sm text-slate-500">{hint}</p>}
    <div className="mt-4 space-y-3">{children}</div>
  </div>
);

const WelcomeFields = ({ label, value, onChange, testId }) => (
  <div className="space-y-2 rounded-2xl bg-slate-50 p-4">
    <Label>{label}</Label>
    <Input value={value.title} onChange={(e) => onChange({ ...value, title: e.target.value })} placeholder="Title" data-testid={`${testId}-title`} />
    <Textarea rows={3} value={value.message} onChange={(e) => onChange({ ...value, message: e.target.value })} placeholder="Message" data-testid={`${testId}-message`} />
  </div>
);

const SlideRow = ({ i, slide, version, onChange, reload }) => {
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { await staffApi().post(`/content/slides/${i}/image`, fd); toast.success("Slide image updated"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const reset = async () => {
    try { await staffApi().delete(`/content/slides/${i}/image`); toast.success("Default image restored"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-3 rounded-2xl bg-slate-50 p-3 sm:grid-cols-[140px_1fr]" data-testid={`content-slide-${i}`}>
      <img src={version ? `${API}/public/slides/${i}/image?v=${version}` : DEFAULT_SLIDE_IMAGES[i]} alt="" className="aspect-video w-full rounded-xl object-cover" />
      <div className="space-y-2">
        <Input value={slide.caption} onChange={(e) => onChange({ ...slide, caption: e.target.value })} placeholder="Headline" data-testid={`content-slide-${i}-caption`} />
        <Input value={slide.subcaption} onChange={(e) => onChange({ ...slide, subcaption: e.target.value })} placeholder="Second line" data-testid={`content-slide-${i}-subcaption`} />
        <div className="flex flex-wrap gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium hover:border-[#1D64B4]">
            <Upload className="h-3.5 w-3.5" /> Change image
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`content-slide-${i}-file`} />
          </label>
          {version && <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-rose-500" onClick={reset} data-testid={`content-slide-${i}-reset`}>Use default</Button>}
        </div>
      </div>
    </div>
  );
};

export const ContentExtras = ({ f, setF, content }) => {
  const setVideo = (i, k, v) => setF({ ...f, past_services: f.past_services.map((s, j) => (j === i ? { ...s, [k]: v } : s)) });
  return (
    <>
      <Card title="Hero slider" hint="Three slides that play like a video on the front page. Saved captions apply when you press Save; images update right away.">
        {f.slides.map((s, i) => (
          <SlideRow key={i} i={i} slide={s} version={content.slide_images?.[i]} reload={content.reload}
            onChange={(v) => setF({ ...f, slides: f.slides.map((x, j) => (j === i ? v : x)) })} />
        ))}
      </Card>
      <Card title="Welcome messages" hint="Shown on the front page.">
        <WelcomeFields label="For new visitors and seekers" value={f.welcome_new} onChange={(v) => setF({ ...f, welcome_new: v })} testId="content-welcome-new" />
        <WelcomeFields label="For regular attendees and members" value={f.welcome_member} onChange={(v) => setF({ ...f, welcome_member: v })} testId="content-welcome-member" />
      </Card>
      <Card title="Current series" hint="Shown on the Worship Services page.">
        <Input value={f.current_series.title} onChange={(e) => setF({ ...f, current_series: { ...f.current_series, title: e.target.value } })} placeholder="Series title" data-testid="content-series-title" />
        <Textarea rows={3} value={f.current_series.description} onChange={(e) => setF({ ...f, current_series: { ...f.current_series, description: e.target.value } })} placeholder="Short description" data-testid="content-series-description" />
      </Card>
      <Card title="Past services (YouTube)" hint="Paste links from the Victory Parañaque YouTube channel. Newest first.">
        {f.past_services.map((v, i) => (
          <div key={i} className="grid gap-2 sm:grid-cols-[1.4fr_1fr_auto]" data-testid={`content-video-${i}`}>
            <Input value={v.url} onChange={(e) => setVideo(i, "url", e.target.value)} placeholder="https://youtube.com/watch?v=..." data-testid={`content-video-${i}-url`} />
            <Input value={v.title} onChange={(e) => setVideo(i, "title", e.target.value)} placeholder="Title (optional)" data-testid={`content-video-${i}-title`} />
            <Button size="icon" variant="ghost" onClick={() => setF({ ...f, past_services: f.past_services.filter((_, j) => j !== i) })} className="text-rose-500" data-testid={`content-video-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => setF({ ...f, past_services: [{ url: "", title: "" }, ...f.past_services] })} data-testid="content-video-add"><Plus className="mr-1 h-4 w-4" /> Add video</Button>
      </Card>
    </>
  );
};
